/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { TableSection } from "./TableSection";
import styles from "./Table.module.css";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { Container, Grid2 } from "@mui/material";

export const Table = ({ applicationList, deleteApp, isOemMode = false }) => {
  const handleDelete = (props) => {
    if (deleteApp) deleteApp(props);
  };
  const [currentApps, setCurrentApps] = useState([]);
  const [sorting, setSorting] = useState({
    key: "lastModified",
    ascending: false,
  });

  function applySorting(key, ascending) {
    setSorting({ key: key, ascending: ascending });
  }

  useEffect(() => {
    if (applicationList.length > 0) {
      const currentApplicationCopy = [...applicationList];

      const sortedCurrentApps = currentApplicationCopy.toSorted((a, b) => {
        return a[sorting.key].localeCompare(b[sorting.key]);
      });

      setCurrentApps(
        sorting.ascending ? sortedCurrentApps : sortedCurrentApps.toReversed()
      );
    }
  }, [sorting, applicationList]);

  return (
    <Container maxWidth="lg">
      <Grid2 container>
        <Grid2 size={12}></Grid2>
        <table>
          <thead className={styles.tableHeaderCls}>
            <tr>
              <th></th>
              <th
                onClick={() =>
                  applySorting("applicationName", !sorting.ascending)
                }
              >
                Application Name
                {sorting.key === "applicationName" && (
                  <span>
                    {!sorting.ascending && (
                      <ArrowUpwardIcon className={styles.arrowSortCls} />
                    )}
                    {sorting.ascending && (
                      <ArrowDownwardIcon className={styles.arrowSortCls} />
                    )}
                  </span>
                )}
                {sorting.key !== "applicationName" && (
                  <SwapVertIcon className={styles.arrowSortCls1} />
                )}
              </th>
              <th>Version</th>
              <th>Status</th>
              {!isOemMode && (
                <th
                  onClick={() => applySorting("lastModified", !sorting.ascending)}
                  style={{ whiteSpace: "nowrap" }}
                >
                  Last Modified
                  {sorting.key === "lastModified" && (
                    <span>
                      {sorting.ascending && (
                        <ArrowDownwardIcon className={styles.arrowSortCls1} />
                      )}
                      {!sorting.ascending && (
                        <ArrowUpwardIcon className={styles.arrowSortCls1} />
                      )}
                    </span>
                  )}
                  {sorting.key !== "lastModified" && (
                    <SwapVertIcon className={styles.arrowSortCls1} />
                  )}
                </th>
              )}
              {!isOemMode && <th>Actions</th>}
            </tr>
          </thead>

          {applicationList.length === 0 && sorting && (
            <tbody>
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  No Data found
                </td>
              </tr>
            </tbody>
          )}
          {applicationList.length > 0 && !sorting && (
            <tbody>
              {applicationList.map((application, index) => (
                <TableSection
                  key={application.packageId || index}
                  item={application}
                  index={index}
                  deleteItem={handleDelete}
                  type="application"
                  isOemMode={isOemMode}
                />
              ))}
            </tbody>
          )}

          {applicationList.length > 0 && currentApps.length > 0 && sorting && (
            <tbody>
              {currentApps.map((application, index) => (
                <TableSection
                  key={application.packageId || index}
                  item={application}
                  index={index}
                  deleteItem={handleDelete}
                  type="application"
                  isOemMode={isOemMode}
                />
              ))}
            </tbody>
          )}
        </table>
      </Grid2>
    </Container>
  );
};

Table.propTypes = {
  applicationList: PropTypes.array.isRequired,
  deleteApp: PropTypes.func,
  isOemMode: PropTypes.bool,
};
